/* eslint-disable @typescript-eslint/no-explicit-any */
import { Text, Spacer, Flex, Spinner } from "@hackthenorth/north";
import { GraphQLError } from "graphql";
import { TextInput, Select, Checkbox, Button } from "north.js";
import React, { useEffect, useRef, useState } from "react";
import { Prompt } from "react-router-dom";
import styled from "styled-components";
import "twin.macro";

import {
  FormFieldLabelRow,
  OptionalLabel,
  PageWrapper,
} from "src/shared/components";
import { successToast, errorToast } from "src/shared/components/Toast";
import { useHackerContext, useUserContext } from "src/shared/contexts";
import { Field, PageStage } from "src/shared/contexts/HackerContext/types";
import { useHackerState } from "src/shared/contexts/HackerContext/useHackerState";
import { useUpdateMyProfileMutation } from "src/shared/contexts/UserContext/graphql/updateMyProfile.generated";
import { useDeviceSize, useIsMounted } from "src/shared/hooks";
// import { answersToOptionsNew } from "src/shared/utils/react-select";
import { answersToNorthV2Options } from "src/shared/utils/react-select";
import { Nullable } from "src/shared/utils/typescript";
import {
  isEmail,
  isPhoneNumber,
  notNull,
  isWaterlooIDNumber,
} from "src/shared/utils/validation";
import { formatValidationIssues } from "src/shared/utils/validationMessages";
import { ArrowRight } from "src/static/icons";

import { formatBirthDate, isAdultOnDate } from "../confirmation/waiver";
import RSVPSidebar from "../rsvp-sidebar";

import { ACCEPTED_FIELDS, VALIDATORS } from "./constants";
import { buildPersonalPrefillValues, PersonalPrefillAnswers } from "./prefill";
import { buildAccountProfileNames } from "./profileSync";
import SizeChart from "./SizeChart";

const PRONOUN_OPTIONS = ["they/them", "she/her", "he/him", "other"];
const UNISEX_SIZE_OPTIONS = ["XS", "S", "M", "L", "XL", "2XL"];
const FOOD_DIETARY_OPTIONS = [
  "Vegetarian",
  "Vegan",
  "Halal",
  "Gluten-free",
  "Nut-free",
  "Lactose intolerant",
  "Other",
];

const isEmpty = (s: Nullable<string> | undefined) =>
  s == null || s.trim() === "";

const PersonalPage = () => {
  const isTabletOrSmaller = useDeviceSize("tablet");
  const isMounted = useIsMounted();
  const {
    updateResponses,
    isLoading,
    isReadOnly,
    navigateNext,
    claimData,
    hasUnsavedAutosave,
  } = useHackerContext();
  const { info } = useUserContext();
  const { responsesState, setResponsesState, isValid, isResponseUpdated } =
    useHackerState(ACCEPTED_FIELDS, VALIDATORS);
  const [updateMyProfile, { loading: isProfileUpdating }] =
    useUpdateMyProfileMutation();

  const [showErrors, setShowErrors] = useState(false);
  const [isBlocking, setIsBlocking] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const hasSeededPrefill = useRef(false);

  useEffect(() => {
    if (isValid && isMounted) {
      navigateNext();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [submitting]);

  useEffect(() => {
    if (!isResponseUpdated || info == null || hasSeededPrefill.current) return;

    hasSeededPrefill.current = true;
    const current: PersonalPrefillAnswers = {
      [Field.LEGAL_NAME]: responsesState[Field.LEGAL_NAME] as string | null,
      [Field.SHORT_BIRTHDATE]: responsesState[Field.SHORT_BIRTHDATE] as
        | string
        | null,
      [Field.PREFERRED_NAME]: responsesState[Field.PREFERRED_NAME] as
        | string
        | null,
      [Field.PRONOUNS]: responsesState[Field.PRONOUNS] as string[] | null,
      [Field.PHONE_NUMBER]: responsesState[Field.PHONE_NUMBER] as string | null,
    };
    const next = buildPersonalPrefillValues(current, {
      applicationName: claimData[Field.SHORT_NAME] as string | null,
      accountName: info.name,
      accountLegalName: info.legalName,
      accountBirthDate: info.dateOfBirth,
      accountPhone: info.phoneNumber,
    });

    (Object.keys(next) as Field[]).forEach((field) => {
      if (next[field] !== current[field]) {
        setResponsesState(field, next[field]);
      }
    });
    // Only seed once, after both claim and account data have hydrated.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isResponseUpdated, info]);

  // Valid only if they've entered something
  const isLegalFullNameValid = !isEmpty(responsesState[Field.LEGAL_NAME]);
  const isDisplayNameValid = !isEmpty(responsesState[Field.PREFERRED_NAME]);
  const birthDate = responsesState[Field.SHORT_BIRTHDATE] ?? "";
  const isBirthDateValid = isAdultOnDate(birthDate) !== null;
  const isPhoneNumberValid = isPhoneNumber(responsesState[Field.PHONE_NUMBER]);
  const isWaterlooStudentIDValid = isWaterlooIDNumber(
    responsesState[Field.WATERLOO_STUDENT_ID_NUMBER]
  );
  const isEmergencyContactNameValid = !isEmpty(
    responsesState[Field.EMERGENCY_CONTACT_NAME]
  );
  const isEmergencyContactRelationshipValid = !isEmpty(
    responsesState[Field.EMERGENCY_CONTACT_RELATIONSHIP]
  );
  const isEmergencyContactPhoneNumberValid = isPhoneNumber(
    responsesState[Field.EMERGENCY_CONTACT_PHONE_NUMBER]
  );
  const isEmergencyContactEmailValid =
    isEmail(responsesState[Field.EMERGENCY_CONTACT_EMAIL]) &&
    !isEmpty(responsesState[Field.EMERGENCY_CONTACT_EMAIL]);
  const isShirtSizeValid = notNull(responsesState[Field.SHIRT_SIZE]);

  const OTHER_OPTION = "Other: ";

  const containsOther = (arr: Nullable<string[]>) => {
    if (!arr || arr.length === 0) return false;
    return arr[0].includes(OTHER_OPTION);
  };

  const getOtherVal = (arr: Nullable<string[]>) => {
    if (!arr || arr.length === 0) return "";
    return arr[0].substring(OTHER_OPTION.length);
  };

  const onSave = async () => {
    setShowErrors(true);
    if (isValid && info != null) {
      try {
        const { errors } = await updateResponses({
          ...responsesState,
          [Field.SHIRT_TYPE]: "unisex",
          [Field.PERSONAL_RSVP_STAGE]: PageStage.COMPLETED,
        });

        if (errors) {
          throw new Error(
            "We weren't able to submit your answers, please try again."
          );
        }

        const { displayName, legalName } = buildAccountProfileNames(
          responsesState[Field.LEGAL_NAME],
          responsesState[Field.PREFERRED_NAME]
        );

        try {
          const { errors: profileErrors } = await updateMyProfile({
            variables: {
              name: displayName,
              legal_name: legalName,
            },
          });

          if (profileErrors?.length) {
            throw profileErrors[0];
          }
        } catch {
          throw new Error(
            "Your RSVP answers were saved, but we weren't able to update your dashboard name. Please try saving again."
          );
        }

        successToast("Answers successfully saved!");
        setSubmitting(true);
      } catch (e) {
        if (typeof e === "object") {
          errorToast((e as GraphQLError)?.message);
        } else {
          errorToast(e);
        }
      }
    } else if (!isValid) {
      window.scrollTo(0, 0);
      errorToast(
        formatValidationIssues([
          { label: "Full legal name", isValid: isLegalFullNameValid },
          { label: "Date of birth", isValid: isBirthDateValid },
          { label: "Display name", isValid: isDisplayNameValid },
          { label: "Phone number", isValid: isPhoneNumberValid },
          {
            label: "Waterloo student ID",
            isValid: isWaterlooStudentIDValid,
          },
          {
            label: "Emergency contact name",
            isValid: isEmergencyContactNameValid,
          },
          {
            label: "Emergency contact relationship",
            isValid: isEmergencyContactRelationshipValid,
          },
          {
            label: "Emergency contact phone number",
            isValid: isEmergencyContactPhoneNumberValid,
          },
          {
            label: "Emergency contact email",
            isValid: isEmergencyContactEmailValid,
          },
          { label: "Shirt size", isValid: isShirtSizeValid },
        ])
      );
    } else {
      errorToast(
        "Your account information is still loading. Please try again."
      );
    }
  };

  if (responsesState[Field.SHIRT_TYPE] !== "unisex") {
    setResponsesState(Field.SHIRT_TYPE, "unisex");
  }
  if (!isShirtSizeValid) {
    setResponsesState(Field.SHIRT_SIZE, "XS");
  }

  return (
    <PageWrapper>
      <Prompt
        when={isBlocking && hasUnsavedAutosave && !submitting}
        message="You have unsaved changes, are you sure you want to move away from this page?"
      />
      {isTabletOrSmaller && <RSVPSidebar currentRoute="Personal Information" />}
      <Title>Personal Information</Title>
      <Text mods="grey">
        All questions are required unless stated otherwise.
      </Text>
      <Spacer height={42} />

      <Heading>General</Heading>
      <QuestionWrapper>
        <Text mods="bold textBody">Full legal name</Text>
        <Spacer height={12} />
        <TextInputRSVP
          disabled={isReadOnly}
          placeholder="Enter your full legal name"
          value={responsesState[Field.LEGAL_NAME] as any}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            setResponsesState(Field.LEGAL_NAME, e.target.value);
            setIsBlocking(true);
          }}
          error={
            showErrors &&
            !isLegalFullNameValid &&
            "Please enter your full legal name."
          }
          hint="We use your legal name to confirm your ID during registration."
        />
      </QuestionWrapper>
      <QuestionWrapper>
        <Text mods="bold textBody">Date of birth</Text>
        <Spacer height={12} />
        <TextInputRSVP
          type="date"
          disabled={isReadOnly}
          value={formatBirthDate(birthDate)}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            setIsBlocking(true);
            setResponsesState(Field.SHORT_BIRTHDATE, e.currentTarget.value);
          }}
          error={
            showErrors &&
            !isBirthDateValid &&
            "Please confirm your date of birth."
          }
        />
      </QuestionWrapper>
      <QuestionWrapper>
        <Text mods="bold textBody">Display name</Text>
        <Spacer height={12} />
        <TextInputRSVP
          disabled={isReadOnly}
          maxLength={32}
          placeholder="Enter your display name"
          value={responsesState[Field.PREFERRED_NAME] as any}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            setResponsesState(Field.PREFERRED_NAME, e.target.value);
            setIsBlocking(true);
          }}
          error={
            showErrors &&
            !isDisplayNameValid &&
            "Please enter your display name."
          }
          hint="This is the name we'll call you across our tools. For most people, that's their first name."
        />
      </QuestionWrapper>

      <QuestionWrapper>
        <FormFieldLabelRow>
          <Text mods="bold textBody">Pronouns</Text>
          <OptionalLabel>Optional</OptionalLabel>
        </FormFieldLabelRow>
        <Spacer height={12} />
        {PRONOUN_OPTIONS.map((option, index) => (
          <CheckboxRSVP
            key={index}
            label={option}
            checked={
              index === PRONOUN_OPTIONS.length - 1
                ? containsOther(responsesState[Field.PRONOUNS])
                : (responsesState[Field.PRONOUNS] || []).includes(option)
            }
            value={option}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              if (index === PRONOUN_OPTIONS.length - 1) {
                if (e.target.checked) {
                  setResponsesState(Field.PRONOUNS, [
                    OTHER_OPTION,
                    ...(responsesState[Field.PRONOUNS] || []),
                  ]);
                } else {
                  setResponsesState(
                    Field.PRONOUNS,
                    (responsesState[Field.PRONOUNS] || [""]).slice(1)
                  );
                }
              } else {
                if (e.target.checked) {
                  setResponsesState(Field.PRONOUNS, [
                    ...(responsesState[Field.PRONOUNS] || []),
                    option,
                  ]);
                } else {
                  setResponsesState(
                    Field.PRONOUNS,
                    (responsesState[Field.PRONOUNS] || []).filter(
                      (e) => e !== option
                    )
                  );
                }
              }
              setIsBlocking(true);
            }}
          />
        ))}
        {containsOther(responsesState[Field.PRONOUNS]) && (
          <CheckboxTextInputWrapper>
            <TextInputRSVP
              disabled={isReadOnly}
              placeholder="Please enter pronouns"
              value={getOtherVal(responsesState[Field.PRONOUNS])}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                setResponsesState(Field.PRONOUNS, [
                  OTHER_OPTION + e.target.value,
                  ...(responsesState[Field.PRONOUNS] || [""]).slice(1),
                ]);
                setIsBlocking(true);
              }}
              hint={"Separate sets of pronouns by a comma."}
            />
          </CheckboxTextInputWrapper>
        )}
      </QuestionWrapper>
      <QuestionWrapper>
        <Text mods="bold textBody">Phone number</Text>
        <Spacer height={12} />
        <TextInputRSVP
          type="tel"
          disabled={isReadOnly}
          maxLength={30}
          placeholder="Enter your phone number"
          value={responsesState[Field.PHONE_NUMBER] as any}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            setResponsesState(Field.PHONE_NUMBER, e.target.value);
            setIsBlocking(true);
          }}
          hint={
            "If you’re not from Canada or the US, please also enter your country calling code. We may need to contact you by phone for urgent matters."
          }
          error={
            showErrors &&
            !isPhoneNumberValid &&
            "Please enter a valid phone number"
          }
        />
        <Spacer height={8} />
      </QuestionWrapper>
      <QuestionWrapper>
        <FormFieldLabelRow>
          <Text mods="bold textBody">Waterloo student ID number</Text>
          <OptionalLabel>Optional</OptionalLabel>
        </FormFieldLabelRow>
        <Spacer height={12} />
        <TextInputRSVP
          disabled={isReadOnly}
          maxLength={8}
          placeholder="Enter your Waterloo student ID number"
          value={responsesState[Field.WATERLOO_STUDENT_ID_NUMBER] as any}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            setResponsesState(Field.WATERLOO_STUDENT_ID_NUMBER, e.target.value);
            setIsBlocking(true);
          }}
          hint={
            "Only University of Waterloo students should fill out this field."
          }
          error={
            showErrors &&
            !isWaterlooStudentIDValid &&
            "Please enter a valid Waterloo student ID number."
          }
        />
        <Spacer height={8} />
      </QuestionWrapper>
      <QuestionWrapper>
        <Text mods="bold textBody">Emergency contact name</Text>
        <Spacer height={12} />
        <TextInputRSVP
          disabled={isReadOnly}
          placeholder="Enter the name of your emergency contact"
          value={responsesState[Field.EMERGENCY_CONTACT_NAME] as any}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            setResponsesState(Field.EMERGENCY_CONTACT_NAME, e.target.value);
            setIsBlocking(true);
          }}
          hint={"We will contact them in case of emergencies."}
          error={
            showErrors &&
            !isEmergencyContactNameValid &&
            "Please enter the name of your emergency contact."
          }
        />
      </QuestionWrapper>
      <QuestionWrapper>
        <Text mods="bold textBody">Emergency contact relationship</Text>
        <Spacer height={12} />
        <TextInputRSVP
          disabled={isReadOnly}
          placeholder="Enter the type of relationship you have with your emergency contact"
          value={responsesState[Field.EMERGENCY_CONTACT_RELATIONSHIP] as any}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            setResponsesState(
              Field.EMERGENCY_CONTACT_RELATIONSHIP,
              e.target.value
            );
            setIsBlocking(true);
          }}
          error={
            showErrors &&
            !isEmergencyContactRelationshipValid &&
            "Please enter the type of relationship you have with your emergency contact."
          }
        />
      </QuestionWrapper>
      <QuestionWrapper>
        <Text mods="bold textBody">Emergency contact phone number</Text>
        <Spacer height={12} />
        <TextInputRSVP
          type="tel"
          disabled={isReadOnly}
          maxLength={30}
          placeholder="Enter your emergency contact’s phone number"
          value={responsesState[Field.EMERGENCY_CONTACT_PHONE_NUMBER] as any}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            setResponsesState(
              Field.EMERGENCY_CONTACT_PHONE_NUMBER,
              e.target.value
            );
            setIsBlocking(true);
          }}
          hint={
            "If your emergency contact is not from Canada or the US, please also enter their country calling code."
          }
          error={
            showErrors &&
            !isEmergencyContactPhoneNumberValid &&
            "Please enter a valid phone number"
          }
        />
        <Spacer height={8} />
      </QuestionWrapper>
      <QuestionWrapper>
        <Text mods="bold textBody">Emergency contact email</Text>
        <Spacer height={12} />
        <TextInputRSVP
          disabled={isReadOnly}
          placeholder="Enter your emergency contact’s email"
          value={responsesState[Field.EMERGENCY_CONTACT_EMAIL] as any}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            setResponsesState(Field.EMERGENCY_CONTACT_EMAIL, e.target.value);
            setIsBlocking(true);
          }}
          error={
            showErrors &&
            !isEmergencyContactEmailValid &&
            "Please enter a valid email"
          }
        />
        <Spacer height={8} />
      </QuestionWrapper>
      <QuestionWrapper>
        <FormFieldLabelRow>
          <Text mods="bold textBody">
            Do you require any special accommodations for our event?
          </Text>
          <OptionalLabel>Optional</OptionalLabel>
        </FormFieldLabelRow>
        <Spacer height={12} />
        <TextInputRSVP
          disabled={isReadOnly}
          placeholder="Enter any accommodations here"
          value={responsesState[Field.ACCESSIBILITY_NEEDS] as any}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            setResponsesState(Field.ACCESSIBILITY_NEEDS, e.target.value);
            setIsBlocking(true);
          }}
          hint={
            "We want to ensure Hack the North is inclusive for everyone. If there are any accommodations we should be aware of please let us know!"
          }
        />
      </QuestionWrapper>

      <Divider />
      <Heading>Swag</Heading>
      <Text mods="bold textBody">Sizing chart</Text>
      <Spacer height={12} />
      <SizeChartWrapper>
        <SizeChart />
      </SizeChartWrapper>
      <Spacer height={isTabletOrSmaller ? 0 : 24} />
      <ShirtSizeWrapper>
        <Text mods="bold textBody">Shirt size</Text>
        <Spacer height={12} />
        <SelectRSVP
          disabled={isReadOnly}
          fullWidth
          size="lg"
          placeholder="Select a shirt size"
          value={responsesState[Field.SHIRT_SIZE] || ""}
          onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
            setResponsesState(Field.SHIRT_SIZE, e.target.value);
            setIsBlocking(true);
          }}
          error={showErrors && !isShirtSizeValid && "Please select an option."}
        >
          {answersToNorthV2Options(UNISEX_SIZE_OPTIONS)}
        </SelectRSVP>
      </ShirtSizeWrapper>
      <QuestionWrapper>
        <FormFieldLabelRow>
          <Text mods="bold textBody">
            Do you have any apparel accommodation requests?
          </Text>
          <OptionalLabel>Optional</OptionalLabel>
        </FormFieldLabelRow>
        <Spacer height={12} />
        <TextInputRSVP
          disabled={isReadOnly}
          placeholder="Enter any apparel accomodation requests (ex. long sleeves)"
          value={responsesState[Field.SWAG_SHIRT_ACCOMODATIONS] as any}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            setResponsesState(Field.SWAG_SHIRT_ACCOMODATIONS, e.target.value);
            setIsBlocking(true);
          }}
        />
      </QuestionWrapper>
      <Divider />
      <Heading>Food</Heading>
      <QuestionWrapper>
        <FormFieldLabelRow>
          <Text mods="bold textBody">
            Please select any of the following dietary restrictions you may
            have.
          </Text>
          <OptionalLabel>Optional</OptionalLabel>
        </FormFieldLabelRow>
        <Spacer height={12} />
        {FOOD_DIETARY_OPTIONS.map((option, index) => (
          <CheckboxRSVP
            key={index}
            label={option}
            checked={
              index === FOOD_DIETARY_OPTIONS.length - 1
                ? containsOther(responsesState[Field.FOOD_DIETARY_RESTRICTIONS])
                : (
                    responsesState[Field.FOOD_DIETARY_RESTRICTIONS] || []
                  ).includes(option)
            }
            value={option}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              if (index === FOOD_DIETARY_OPTIONS.length - 1) {
                if (e.target.checked) {
                  setResponsesState(Field.FOOD_DIETARY_RESTRICTIONS, [
                    OTHER_OPTION,
                    ...(responsesState[Field.FOOD_DIETARY_RESTRICTIONS] || []),
                  ]);
                } else {
                  setResponsesState(
                    Field.FOOD_DIETARY_RESTRICTIONS,
                    (
                      responsesState[Field.FOOD_DIETARY_RESTRICTIONS] || [""]
                    ).slice(1)
                  );
                }
              } else {
                if (e.target.checked) {
                  setResponsesState(Field.FOOD_DIETARY_RESTRICTIONS, [
                    ...(responsesState[Field.FOOD_DIETARY_RESTRICTIONS] || []),
                    option,
                  ]);
                } else {
                  setResponsesState(
                    Field.FOOD_DIETARY_RESTRICTIONS,
                    (
                      responsesState[Field.FOOD_DIETARY_RESTRICTIONS] || []
                    ).filter((e) => e !== option)
                  );
                }
              }
              setIsBlocking(true);
            }}
          />
        ))}
        {/* {responsesState[Field.DIETARY_OPTIONAL_SELECTED] && ( */}
        {containsOther(responsesState[Field.FOOD_DIETARY_RESTRICTIONS]) && (
          <CheckboxTextInputWrapper>
            <TextInputRSVP
              disabled={isReadOnly}
              placeholder="Please enter restrictions"
              value={getOtherVal(
                responsesState[Field.FOOD_DIETARY_RESTRICTIONS]
              )}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                setResponsesState(Field.FOOD_DIETARY_RESTRICTIONS, [
                  OTHER_OPTION + e.target.value,
                  ...(
                    responsesState[Field.FOOD_DIETARY_RESTRICTIONS] || [""]
                  ).slice(1),
                ]);
                setIsBlocking(true);
              }}
            />
          </CheckboxTextInputWrapper>
        )}
      </QuestionWrapper>
      <Spacer height={50} />
      <FlexRow>
        <Button
          color="primary"
          disabled={isLoading || isProfileUpdating || info == null}
          onClick={onSave}
          size="md"
        >
          {isLoading || isProfileUpdating ? (
            <Spinner />
          ) : (
            <FlexRow>
              <p>
                {responsesState[Field.PERSONAL_RSVP_STAGE] ===
                PageStage.COMPLETED
                  ? "Save"
                  : "Next"}
              </p>
              <Spacer width="8px" />
              <ArrowRight width={16} />
            </FlexRow>
          )}
        </Button>
        <Spacer width="32px" />
      </FlexRow>
    </PageWrapper>
  );
};

const ShirtSizeWrapper = styled.div`
  width: calc(50% - 20px);
  margin-bottom: 36px;

  ${({ theme }) => theme.mediaQueries.tablet`
    width: 100%;
  `}
`;

const Title = styled.h1`
  font-size: 28px;
  font-weight: 900;
  font-family: ${({ theme }) => theme.globalConstants.fontFamily.heading};
  line-height: 1.5;
  color: ${({ theme }) => theme.globalConstants.color.brandSecondary};
  margin-bottom: 8px;
  margin-top: 0;
`;

const Heading = styled.h3`
  font-size: 20px;
  font-weight: 900;
  font-family: ${({ theme }) => theme.globalConstants.fontFamily.heading};
  line-height: 1.5;
  color: ${({ theme }) => theme.globalConstants.color.brandSecondary};
  margin-bottom: 24px;
`;

const CheckboxTextInputWrapper = styled.div`
  margin-top: 8px;
  margin-left: 24px;
`;

const QuestionWrapper = styled.div`
  width: 100%;
  margin-bottom: 36px;
`;

const TextInputRSVP = styled(TextInput).attrs({ size: "md" })`
  font-size: 16px !important;
  width: 100%;
`;

const SelectRSVP = styled(Select).attrs({ size: "lg" })`
  font-size: 16px !important;
`;

const CheckboxRSVP = styled(Checkbox)`
  font-size: 16px;
`;

const Divider = styled.div`
  width: 100%;
  height: 1px;
  margin: 42px 0;
  background-color: ${({ theme }) => theme.globalConstants.color.borderGray};
`;

const FlexRow = styled.div`
  display: flex;
  align-items: center;
`;

const SizeChartWrapper = styled(Flex).attrs({
  justify: "flex-start",
  wrap: "wrap",
})`
  column-gap: 40px;
  row-gap: 24px;
`;

export default PersonalPage;
